import { CancelToken } from 'axios';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { getSupplierCarList, selectSupplierCarList, supplierCarListActions } from '../../store/entities/supplierCarList';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { Grid } from '../common/grid/Grid';
import { SectionHeader } from '../common/SectionHeader';
import { SupplierCarDto } from '@api/models/suppliers/SupplierCarDto';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { InnerTooltipWrapper, QcsTooltip } from '../common/basic/QcsTooltip';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { hasRole } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { supplierCarService } from '@api/services/services';
import { getLanguageVariantName } from '../../utils/format';
import { UserRole } from '@api/models/enums/UserRole';

interface SupplierCarsProps {
  supplierId: string;
}

export const SupplierCars: FC<SupplierCarsProps> = ({ supplierId }) => {
  const { t, i18n } = useTranslation();
  const supplierCarList = useAppSelector(selectSupplierCarList);
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const identity = useAppSelector(selectIdentity);

  useEffect(() => {
    dispatch(supplierCarListActions.reload(true));
  }, [dispatch]);

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getSupplierCarList(supplierId, cancelToken));
  };

  const handleRowClick = (item: SupplierCarDto) => {
    navigate(PathKey.SupplierCarDetail, { supplierId, carId: item.id! });
  };

  const handleAddClick = () => {
    navigate(PathKey.SupplierCarNew, { supplierId });
  };

  const handleDeleteActivity = async (item: SupplierCarDto) => {
    await supplierCarService.deleteSupplierCar(item.id!);
    dispatch(supplierCarListActions.reload(true));
  };

  const canDelete = useMemo(() => hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.AdminQcs, UserRole.AdminCompany]), [identity]);

  const handleRenderData = (item: SupplierCarDto) => {
    return (
      <>
        <QcsTableCell>{item.spz}</QcsTableCell>
        <QcsTableCell>{getLanguageVariantName(item.type?.languageVariants, i18n.language)}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.accessAllowed ? t('common.yes') : t('common.no')}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.note}</QcsTableCell>
        {canDelete && (
          <QcsTableCell align="right">
            <QcsTooltip title={t('supplier.car.deleteCar')} placement="top">
              <InnerTooltipWrapper>
                <QcsLoadingButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteActivity(item);
                  }}
                >
                  <DeleteForeverIcon />
                </QcsLoadingButton>
              </InnerTooltipWrapper>
            </QcsTooltip>
          </QcsTableCell>
        )}
      </>
    );
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <SectionHeader title="supplier.cars" variant="h5" addText="supplier.car.add" handleAddClick={handleAddClick} />
      <Grid<SupplierCarDto>
        headers={[
          { captionStr: 'supplier.car.spz', orderName: 'spz' },
          { captionStr: 'supplier.car.type', orderName: 'type' },
          { captionStr: 'supplier.car.accessAllowed', orderName: 'accessAllowed' },
          {
            captionStr: 'supplier.car.note',
            hideOnMobile: true,
          },
          ...(canDelete ? [{ captionStr: '' }] : []),
        ]}
        data={supplierCarList}
        gridActions={supplierCarListActions}
        renderData={handleRenderData}
        getData={handleGetData}
        onRowClick={handleRowClick}
      />
    </div>
  );
};
